import React, { useState, useEffect, useRef } from "react";
import AxiosInstance from "../axiosInstance";
import AppointmentBookedModal from "./AppointmentBookedModal";
import "./BookAppointmentModal.css";

const BookAppointmentModal = ({
  practitioner,
  selectedSlot,
  selectedDate,
  onClose,
  appointmentType,
  appointmentFee,
  onAppointmentBooked
}) => {
  // Defensive check to prevent errors if practitioner is undefined

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    age: "",
    gender: "",
    weight: "",
    bodyPoints: [],
    preappointmentdetail_id: "",
    pre_appointment_details: [],
    body_chart_image: "",
    date: "",
    time: "",
    patient_address: "",
  });

  const imgRef = useRef(null); // Ref for image
  const containerRef = useRef(null); // Ref for container div
  const chartRef = useRef(null);

  const [isBooked, setIsBooked] = useState(false);
  const [isAgreed, setIsAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [chartScreenshot, setChartScreenshot] = useState(null);
  const [appointmentId, setAppointmentId] = useState(null); // Store booked appointment ID
  const [fetchingDetails, setFetchingDetails] = useState(false);


  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      date: selectedDate,
      time: selectedSlot,
    }));
  }, [selectedDate, selectedSlot]);

  // **Fetch Charting Details API**
  useEffect(() => {
    if (!practitioner) return;

    const fetchChartingDetails = async () => {
      try {
        const response = await AxiosInstance.get(
          `/patient/charting/get-charting-details/?doctor_id=${practitioner.Health_Professional_Id}`
        );

        if (response.status === 200 && response.data.status) {
          setFormData((prev) => ({
            ...prev,
            preappointmentdetail_id: response.data.data.id,
            body_chart_image: response.data.data.body_chart_image,
            pre_appointment_details: response.data.data.questions.map((q) => ({
              id: q.id,
              question: q.question,
              answer: "",
            })),
          }));
        }
      } catch (error) {
        console.error("Error fetching charting details:", error);
      }
    };

    fetchChartingDetails();
  }, [practitioner && practitioner.Health_Professional_Id]);
  if (!practitioner) return null;
  
  // **Handle Input Changes**
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };


  // **Handle Dynamic Questions Input**
  const handleDynamicInputChange = (id, value) => {
    setFormData((prevData) => ({
      ...prevData,
      pre_appointment_details: prevData.pre_appointment_details.map((q) =>
        q.id === id ? { ...q, answer: value } : q
      ),
    }));
  };


  // **Handle Body Chart Click**
  const handleBodyChartClick = (e) => {
    if (!chartRef.current) return;

    const chartRect = chartRef.current.getBoundingClientRect();
    const x = (e.clientX - chartRect.left) / chartRect.width;
    const y = (e.clientY - chartRect.top) / chartRect.height;

    // Ensure coordinates are within bounds
    const boundedX = Math.max(0, Math.min(1, x));
    const boundedY = Math.max(0, Math.min(1, y));

    setFormData((prevData) => ({
      ...prevData,
      bodyPoints: [
        ...prevData.bodyPoints,
        { x_axis: boundedX, y_axis: boundedY, question: "", answer: "" },
      ],
    }));
  };

  // **Handle Explanation for Body Chart Pins**
  const handleBodyPointExplanationChange = (index, question, answer) => {
    setFormData((prevData) => {
      const updatedBodyPoints = [...prevData.bodyPoints];
      updatedBodyPoints[index] = {
        ...updatedBodyPoints[index],
        question,
        answer,
      };
      return { ...prevData, bodyPoints: updatedBodyPoints };
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Invalid Date"; // Handle empty date

    const dateObj = new Date(dateString);

    if (isNaN(dateObj)) {
      console.error("🚨 Invalid date format received:", dateString);
      return "Invalid Date";
    }

    const month = String(dateObj.getMonth() + 1).padStart(2, "0"); // Ensure 2-digit month
    const day = String(dateObj.getDate()).padStart(2, "0"); // Ensure 2-digit day
    const year = dateObj.getFullYear();

    return `${month}/${day}/${year}`; // Convert to MM/DD/YYYY
  };

  // **Handle Booking API Call**
  const handleBooking = async () => {
    if (!isAgreed) {
      console.warn("📛 Agreement not checked");
      return;
    }

    setLoading(true);

    try {
      // Convert base64 to Blob
      const blob = await fetch(chartScreenshot).then((res) => res.blob());
      if (!practitioner) return null;

      // Create FormData
      const submissionData = new FormData();
      submissionData.append("date", formatDate(selectedDate));
      submissionData.append("time", selectedSlot);
      submissionData.append("doctor_id", practitioner.Health_Professional_Id);
      submissionData.append("gender", formData.gender);
      submissionData.append("full_name", formData.fullName);
      submissionData.append("age", formData.age);
      submissionData.append("weight", formData.weight);
      submissionData.append("email", formData.email);
      submissionData.append("mobile_number", formData.phone);
      submissionData.append(
        "preappointmentdetail_id",
        formData.preappointmentdetail_id
      );
      submissionData.append("body_chart_image", formData.body_chart_image);
      submissionData.append("appointment_type", appointmentType);
      submissionData.append("appointment_fee", appointmentFee);
      submissionData.append("patient_address", formData.patient_address);
      // Convert structured objects to JSON strings
      submissionData.append(
        "pre_appointment_details",
        JSON.stringify(formData.pre_appointment_details)
      );
      submissionData.append("body_points", JSON.stringify(formData.bodyPoints));

      const response = await AxiosInstance.post(
        "/patient/appointment/book-appointment/",
        submissionData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 200 && response.data.status) {
        const bookedAppointmentId = response.data.appointment_id;
        setAppointmentId(bookedAppointmentId);
        setIsBooked(true);
      }
    } catch (error) {
      console.error("Error booking appointment:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseBookedModal = () => {
    setIsBooked(false); // Hide the modal when closed
    onClose(); // Also close the main booking modal
  };

  const handleProceedToStep3 = () => {
    setStep(3);
  };
  

  return (
    <div className="book-appointment-modal-overlay">
      <div className="book-appointment-modal-content">
        {/* Step 1 - User Details */}
        {step === 1 && (
          <div className="book-appointment-modal-step-content">
            <h2>Book Appointment with {practitioner.full_name}</h2>
            <div className="book-appointment-modal-form-group">
              <label>Full Name:</label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="book-appointment-modal-form-group">
              <label>Email:</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="book-appointment-modal-form-group">
              <label>Phone Number:</label>
              <input
                type="phone"
                name="phone"
                value={formData.mobile_number}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="book-appointment-modal-form-group">
              <label>Age:</label>
              <input
                type="number"
                name="age"
                value={formData.age}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="book-appointment-modal-form-group">
              <label>Address:</label>
              <input
                type="address"
                name="patient_address"
                value={formData.patient_address}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="book-appointment-modal-form-group">
              <label>Weight:</label>
              <input
                type="weight"
                name="weight"
                value={formData.weight}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="book-appointment-modal-form-group">
              <label>Gender:</label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
                required
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <button
              className="book-appointment-modal-button"
              onClick={() => setStep(2)}
            >
              Proceed
            </button>
          </div>
        )}

        {/* Step 2 - Dynamic Questions & Body Chart */}
        {step === 2 && (
          <div className="book-appointment-modal-step-content">
            <h3>Fill in the required details</h3>

            {formData.pre_appointment_details.map((q) => (
              <div key={q.id} className="book-appointment-modal-form-group">
                <label>{q.question}</label>
                <textarea
                  value={q.answer}
                  onChange={(e) =>
                    handleDynamicInputChange(q.id, e.target.value)
                  }
                  required
                />
              </div>
            ))}

            {/* **Body Chart Image with Clickable Pins** */}
            <div className="body-chart-container">
              <div
                ref={chartRef}
                style={{ position: "relative", display: "inline-block" }}
                onClick={handleBodyChartClick}
              >
                <img
                  ref={imgRef}
                  src={formData.body_chart_image}
                  className="charting-image"
                  alt="Body Chart"
                  // crossOrigin="anonymous"
                />

                {formData.bodyPoints.map((point, index) => (
                  <div
                    key={index}
                    style={{
                      position: "absolute",
                      top: `${point.y_axis * imgRef.current.height}px`,
                      left: `${point.x_axis * imgRef.current.width}px`,
                      width: "20px",
                      height: "20px",
                      backgroundColor: "#50cada",
                      transform: "translate(-50%, -50%)",
                      borderRadius: "50%",
                      border: "2px solid white",
                      color: "#FFF",
                      zIndex: 5,
                    }}
                  >
                    {index + 1}
                  </div>
                ))}
              </div>
            </div>

            {/* Input Fields for Pins */}
            <div className="body-chart-explanations">
              {formData.bodyPoints.map((point, index) => (
                <div key={index} className="book-appointment-modal-form-group">
                  <label>Explain Point {index + 1}</label>
                  <textarea
                    value={point.answer}
                    onChange={(e) =>
                      handleBodyPointExplanationChange(
                        index,
                        point.question,
                        e.target.value
                      )
                    }
                    required
                  />
                </div>
              ))}
            </div>

            <button
              className="book-appointment-modal-button"
              onClick={handleProceedToStep3}
            >
              Proceed
            </button>
          </div>
        )}
        {/* Step 3 - Confirm Appointment Details */}
        {/* **Appointment Successfully Booked Modal** */}
        {isBooked && (
          <AppointmentBookedModal
            appointment_id={appointmentId}
            onClose={onClose}
            appointmentStart={selectedDate + " " + selectedSlot + ":00"}
            appointmentEnd={selectedDate + " " + selectedSlot + ":00"}  
          />
        )}
        {step === 3 && (
          <div className="book-appointment-modal-step-content">
            <h3>Confirm Appointment Details</h3>
            <p>
              <strong>Practitioner:</strong> {practitioner.Full_Name}
            </p>
            <p>
              <strong>Full Name:</strong> {formData.fullName}
            </p>
            <p>
              <strong>Email:</strong> {formData.email}
            </p>
            <p>
              <strong>Phone:</strong> {formData.phone}
            </p>
            <p>
              <strong>Date:</strong> {selectedDate || "Not selected"}
            </p>
            <p>
              <strong>Time:</strong> {selectedSlot || "Not selected"}
            </p>
            <p>
              <strong>Appointment Type:</strong> {appointmentType}
            </p>
            <p>
              <strong>Appointment Fee:</strong> {appointmentFee}
            </p>

            <div className="book-appointment-modal-terms">
              <input
                type="checkbox"
                checked={isAgreed}
                onChange={() => setIsAgreed(!isAgreed)}
              />
              <label>
                I agree to the{" "}
                <a href="https://virtualtriage.ai/policy" target="blank">
                  Terms and Conditions
                </a>
              </label>
            </div>

            <button
              className="book-appointment-modal-button"
              onClick={handleBooking}
              disabled={!isAgreed || loading}
            >
              {loading ? "Booking..." : "Book Now"}
            </button>
          </div>
        )}
      </div>
      {/* {chartScreenshot && (
        <div style={{ marginTop: "20px" }}>
          <h4>📸 Screenshot Preview:</h4>
          <img
            src={chartScreenshot}
            alt="Screenshot Preview"
            style={{ maxWidth: "100%", border: "1px solid #ccc" }}
          />
        </div>
      )} */}
    </div>
  );
};

export default BookAppointmentModal;
